import { X as Close } from '@styled-icons/bootstrap/X';
import React from 'react';
import styled from 'styled-components';
import { useBrowser } from 'hooks';
import BatchHeader from './BatchHeader';
import { Container, CloseButton, Item, ItemsContainer } from './styles';
import { Calendar3Event } from '@styled-icons/bootstrap/Calendar3Event';
import { BoxArrowInUpRight } from '@styled-icons/bootstrap/BoxArrowInUpRight';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'components/Shared';
import { transformNumberToLocaleString } from 'utils/numbers';
const Weight = styled.div `
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  font-family: Montserrat, sans-serif;
`;
const UnknownWeight = styled.div `
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  font-family: Montserrat, sans-serif;
`;
const LabelWeight = styled.div `
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.backgrounds.grey};
`;
const LabelDate = styled.div `
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  color: ${({ theme }) => theme.palette.grey['A500']};
`;
const ValueDate = styled.div `
  font-size: 16px;
  font-weight: 700;
`;
const LabelDateContainer = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.palette.grey['A500']};
`;
const TimelineContainer = styled.div `
  display: flex;
  align-items: flex-start;
  font-family: Lato, sans-serif;
  color: #000;
`;
const Timeline = styled.div `
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 12px 0 0 10px;
  height: ${({ height }) => height + 8}px;
  width: 100%;
`;
const DashedLine = styled.div `
  position: absolute;
  left: 0;
  transform: translateX(-50%);
  height: 100%;
  width: 2px;
  background-color: transparent;
  border-left: 2px dashed #f4a300;
`;
const BatchActivity = styled.div `
  position: absolute;
  top: ${({ top }) => top}px;
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;

  &:before {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    background-color: #f4a300;
    border-radius: 50%;
    left: 0;
    transform: translateX(-50%);
  }
`;
const Arrow = styled.div `
  width: 11px;
  height: 11px;
  border: 2px solid #f4a300;
  border-left: none;
  border-top: none;
  transform: rotate(45deg);
  position: absolute;
  left: 0;
  top: calc(100% - 10px);
  transform: translateX(-50%) rotate(45deg);
`;
const Activity = styled.div `
  font-weight: 900;
  font-size: 16px;
  margin-left: 16px;
  margin-right: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Date = styled.div `
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const HeaderContainer = styled.div `
  margin-right: 24px;
`;
const BatchTracingContainer = ({ inboundWeight, outboundWeight, dateReceived, dateDispatched, batchActivities, selectedRawMaterial, activityImage, isOverview, hideLocationPin, hasOtherIngredients, onClose, onClick, }) => {
    const { t } = useTranslation();
    const { isMobile } = useBrowser();
    const lineHeight = 28; // Height per timeline item
    const containerHeight = lineHeight * (batchActivities === null || batchActivities === void 0 ? void 0 : batchActivities.length); // Total height based on number of items
    return (React.createElement(Container, { isMobile: isMobile, minWidth: !isMobile && (batchActivities === null || batchActivities === void 0 ? void 0 : batchActivities.length) > 0 && !isOverview
            ? 350
            : undefined },
        React.createElement(HeaderContainer, null,
            React.createElement(BatchHeader, { hasOtherIngredients: hasOtherIngredients, image: activityImage, rawMaterialTitle: selectedRawMaterial === null || selectedRawMaterial === void 0 ? void 0 : selectedRawMaterial.title, noRawMaterialSelected: !selectedRawMaterial, hideLocationPin: hideLocationPin })),
        (!!onClick || !!onClose) && (React.createElement(CloseButton, { onClick: isMobile ? onClick : onClose }, isMobile ? (React.createElement(BoxArrowInUpRight, { width: 24 })) : (React.createElement(Tooltip, { tooltipText: t('close-batch-tracing-tooltip') },
            React.createElement(Close, { width: 24 }))))),
        !!selectedRawMaterial && (React.createElement(React.Fragment, null,
            React.createElement(ItemsContainer, { gap: 8 },
                React.createElement(Item, null,
                    inboundWeight === 0 ? (React.createElement(UnknownWeight, null, t('unknown'))) : (React.createElement(Weight, null,
                        transformNumberToLocaleString(inboundWeight),
                        " kg")),
                    React.createElement(LabelWeight, null, t('inbound-weight'))),
                React.createElement(Item, null,
                    React.createElement(LabelDateContainer, null,
                        React.createElement(Calendar3Event, { width: 13 }),
                        React.createElement(LabelDate, null, t('date-received'))),
                    React.createElement(ValueDate, null, dateReceived))),
            (batchActivities === null || batchActivities === void 0 ? void 0 : batchActivities.length) > 0 && !isOverview && (React.createElement(TimelineContainer, null,
                React.createElement(Timeline, { height: containerHeight },
                    React.createElement(DashedLine, null),
                    batchActivities.map((item, index) => (React.createElement(Tooltip, { tooltipText: `${item.title} ${item.date}`, key: `${item.title}-${item.date}` },
                        React.createElement(BatchActivity, { top: index * lineHeight },
                            React.createElement(Activity, null, item.title),
                            React.createElement(Date, null, item.date))))),
                    React.createElement(Arrow, null)))),
            dateDispatched && !isOverview && (React.createElement(ItemsContainer, { gap: 8, marginTop: 8 },
                React.createElement(Item, null,
                    outboundWeight === 0 ? (React.createElement(UnknownWeight, null, t('unknown'))) : (React.createElement(Weight, null,
                        transformNumberToLocaleString(outboundWeight),
                        " kg")),
                    React.createElement(LabelWeight, null, t('outbound-weight'))),
                React.createElement(Item, null,
                    React.createElement(LabelDateContainer, null,
                        React.createElement(Calendar3Event, { width: 13 }),
                        React.createElement(LabelDate, null, t('date-dispatched'))),
                    React.createElement(ValueDate, null, dateDispatched))))))));
};
export default BatchTracingContainer;
